import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  throwError,
  from,
  of,
} from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { ToastrService } from 'ngx-toastr';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private localization: LocalizationService,
    private toastr: ToastrService,
    private router: Router
  ) { }

  getCurrentLanguage() {
    return this.localization.getCachedLanguage() ?? 'en';
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const token = sessionStorage.getItem('accessToken');

    const excludedEndpoints = [
      '/api/v1/Authentication/Admin/SignIn',
      '/api/v1/Authentication/SignIn',
      '/api/v1/Authentication/Refresh-Token',
      '/api/v1/Authentication/ForgetPassword',
      'api/v1/Authentication/ConfirmCodeForResetPassword',
      'api/v1/Authentication/ResetPassword',
      'api/v1/User/SelfRegistr',
      '/api/v1/Authentication/forgot-password',
      '/TwoFactor/verify',
    ];

    const isExcluded = excludedEndpoints.some((endpoint) =>
      request.url.includes(endpoint)
    );

    if (this.authService.isTokenExpired() && !isExcluded) {
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        return from(this.authService.refreshToken()).pipe(
          switchMap((refreshSuccess) => {
            if (!refreshSuccess || refreshSuccess.data === null || !refreshSuccess.data.accessToken) {
              this.authService.logout();
              return throwError(() => new Error('Refresh token failed'));
            }

            const newToken = refreshSuccess.data.accessToken;
            this.authService.setToken(newToken);
            this.authService.setRefreshToken(refreshSuccess.data.refreshToken);
            this.authService.setTokenExpiration(refreshSuccess.data.expireAt);

            this.refreshTokenSubject.next(newToken);
            this.isRefreshing = false;

            const clonedRequest = this.addToken(request, newToken as string);
            return next.handle(clonedRequest);
          }),
          catchError((error: HttpErrorResponse) => {
            this.isRefreshing = false;
            this.authService.logout();
            return throwError(() => error);
          })
        );
      } else {
        return this.refreshTokenSubject.pipe(
          filter((token) => token !== null),
          take(1),
          switchMap((newToken) => {
            const clonedRequest = this.addToken(request, newToken as string);
            return next.handle(clonedRequest);
          }),
          catchError((error: HttpErrorResponse) => {
            this.handleErrors(error, request);
            return throwError(() => error);
          })
        );
      }
    } else {
      const clonedRequest = token && !isExcluded 
        ? this.addToken(request, token) 
        : this.addLanguageHeader(request);
      return next.handle(clonedRequest).pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleErrors(error, clonedRequest);
          return throwError(() => error);
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string) {
    const currentLang = this.getCurrentLanguage();
    const acceptLanguage = currentLang === 'ar' ? 'ar' : 'en';
    
    // Remove existing Accept-Language if present and set our own
    let headers = request.headers.delete('Accept-Language');
    
    return request.clone({
      headers: headers
        .set('Authorization', 'Bearer ' + token)
        .set('Cache-Control', 'public, max-age=31536000')
        .set('Accept-Language', acceptLanguage),
    });
  }

  private addLanguageHeader(request: HttpRequest<any>): HttpRequest<any> {
    const currentLang = this.getCurrentLanguage();
    const acceptLanguage = currentLang === 'ar' ? 'ar' : 'en';
    
    // Remove existing Accept-Language if present and set our own
    let headers = request.headers.delete('Accept-Language');
    
    return request.clone({
      headers: headers.set('Accept-Language', acceptLanguage),
    });
  }

  private handleErrors(error: HttpErrorResponse | any, request?: HttpRequest<any>) {
    console.log(error, " errors");

    const errorCode = error?.error?.StatusCode || error?.status;
    // Handle 409 status code - refresh token then reload permissions
    if (errorCode === 409) {
      this.handle409Error();
      return;
    }
    // Check if error messages should be suppressed
    const suppressErrorMessage = request?.headers.get('X-Suppress-Error-Message') === 'true';
    
    if (!suppressErrorMessage) {
      const errorsArray = error?.error?.Errors ?? [];
      if (errorsArray.length === 0) {
        errorsArray.push(error?.error?.message ?? error?.error?.Message ?? error?.message ?? error?.Message);
      }
      errorsArray.forEach((err: string) => {
        if (!!err) {
          this.toastr.error(
            err,
            this.getCurrentLanguage() === 'ar' ? 'خطأ' : 'Error'
          );
        }
      });
    }

    if (errorCode === 403 || errorCode === 422) {
      this.authService.invalidateUserPremissions();
      
      // Don't redirect if this is a tenant update request - let the component handle the error
      const requestUrl = request?.url || '';
      const currentUrl = this.router.url || '';

      // Allow callers to suppress automatic redirect (useful for background/fallback calls)
      const suppressRedirect = request?.headers.get('X-Suppress-Redirect') === 'true';
      
      // Check if this is a tenant getById request (Dashboard/{id} endpoint, not List or Statistics)
      // Example: /admin/api/v1/Dashboard/2
      const isTenantGetByIdRequest = requestUrl.includes('/Dashboard/') && 
                                     !requestUrl.includes('/Dashboard/List') &&
                                     !requestUrl.includes('/Dashboard/AdminDashboardStatistics');
      
      // Check if we're on tenant update/edit page
      const isTenantUpdatePage = currentUrl.includes('/tenants/create-update-list/update/') || 
                                 currentUrl.includes('/tenants/create-update-list/edit/');
      
      // Debug logging
      console.log('[AuthInterceptor] 403/422 Error detected');
      console.log('[AuthInterceptor] Request URL:', requestUrl);
      console.log('[AuthInterceptor] Current Router URL:', currentUrl);
      console.log('[AuthInterceptor] isTenantGetByIdRequest:', isTenantGetByIdRequest);
      console.log('[AuthInterceptor] isTenantUpdatePage:', isTenantUpdatePage);
      
      if (isTenantGetByIdRequest || isTenantUpdatePage) {
        // Stay on the page, let the component handle the error
        // Don't redirect to dashboard for tenant update pages
        console.log('[AuthInterceptor] Skipping redirect - letting component handle error');
        return;
      }

      if (suppressRedirect) {
        console.log('[AuthInterceptor] Skipping redirect - suppressed by header');
        return;
      }
      
      console.log('[AuthInterceptor] Redirecting to dashboard');
      this.router.navigate(['dashboard']);
    }
  }

  /**
   * Handle 409 error: Refresh token first, then reload permissions
   * Scenario: User permissions changed in backend, need to refresh token and reload permissions
   */
  private handle409Error(): void {

    // Step 1: Refresh token
    this.authService.refreshToken().pipe(
      switchMap((refreshSuccess) => {
        if (!refreshSuccess || refreshSuccess.data === null || !refreshSuccess.data.accessToken) {
          console.error('[AuthInterceptor] Refresh token failed on 409 - logging out');
          this.authService.logout();
          return throwError(() => new Error('Refresh token failed'));
        }

        // Step 2: Update tokens
        const newToken = refreshSuccess.data.accessToken;
        this.authService.setToken(newToken);
        this.authService.setRefreshToken(refreshSuccess.data.refreshToken);
        this.authService.setTokenExpiration(refreshSuccess.data.expireAt);

        console.log('[AuthInterceptor] Token refreshed successfully on 409');

        // Step 3: Reload permissions after token refresh
        this.authService.invalidateUserPremissions();

        // Step 4: Navigate to dashboard
        this.router.navigate(['dashboard']);

        return of(null);
      }),
      catchError((refreshError: HttpErrorResponse) => {
        console.error('[AuthInterceptor] Error refreshing token on 409:', refreshError);
        // If refresh fails, logout user
        this.authService.logout();
        return throwError(() => refreshError);
      })
    ).subscribe({
      next: () => {
        console.log('[AuthInterceptor] 409 handled successfully - token refreshed and permissions reloaded');
      },
      error: (err) => {
        console.error('[AuthInterceptor] Failed to handle 409:', err);
      }
    });
  }
}
